import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";

function AdminUserForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const userState = location.state?.user;
  const mode = location.state?.mode || (id ? "edit" : "create");
  const isViewMode = mode === "view";

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: userState?.name || "",
    email: userState?.email || "",
  });
  const [selectedRoleIds, setSelectedRoleIds] = useState(
    new Set((userState?.roles || []).map((role) => role.id)),
  );

  const title = useMemo(() => {
    if (isViewMode) return "View Admin User";
    return id ? "Edit Admin User" : "Add Admin User";
  }, [id, isViewMode]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const rolesResponse = await Apis.getRoles(0, 1000, { status: true });
        setRoles(rolesResponse.data.data.roles || []);

        if (id && !userState) {
          const userResponse = await Apis.getAdminUserById(id);
          const user = userResponse.data.data;
          setFormData({
            name: user.name || "",
            email: user.email || "",
          });
          setSelectedRoleIds(new Set((user.roles || []).map((role) => role.id)));
        }
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to load form data.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [id, userState]);

  const toggleRole = (roleId) => {
    if (isViewMode) return;
    setSelectedRoleIds((prevSelected) => {
      const nextSelected = new Set(prevSelected);
      if (nextSelected.has(roleId)) {
        nextSelected.delete(roleId);
      } else {
        nextSelected.add(roleId);
      }
      return nextSelected;
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      Swal.fire("Error", "Name is required.", "error");
      return;
    }

    if (!formData.email.trim()) {
      Swal.fire("Error", "Email is required.", "error");
      return;
    }

    if (selectedRoleIds.size === 0) {
      Swal.fire("Error", "Please select at least one role.", "error");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      roleIds: [...selectedRoleIds],
    };

    if (!id) {
      payload.email = formData.email.trim();
    }

    try {
      setIsSubmitting(true);
      if (id) {
        await Apis.updateAdminUser(id, payload);
        Swal.fire("Success", "Admin user updated successfully.", "success");
      } else {
        await Apis.createAdminUser(payload);
        Swal.fire(
          "Success",
          "Admin user created and emailed successfully.",
          "success",
        );
      }
      navigate("/admin-users");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save admin user.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loader message="Loading admin user form..." />;
  }

  return (
    <div>
      <div className="bg-gray-100 p-4 flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-2xl font-bold">{title}</h2>
        <button
          type="button"
          className="btn btn-reset-filter justify-center"
          onClick={() => navigate("/admin-users")}
        >
          Cancel
        </button>
      </div>

      <div className="bg-white rounded-lg pagination-shadow p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block font-semibold mb-2">Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Enter user name"
              value={formData.name}
              disabled={isViewMode}
              onChange={(event) =>
                setFormData({ ...formData, name: event.target.value })
              }
            />
          </div>
          <div>
            <label className="block font-semibold mb-2">Email</label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Enter user email"
              value={formData.email}
              disabled={isViewMode || Boolean(id)}
              onChange={(event) =>
                setFormData({ ...formData, email: event.target.value })
              }
            />
            {id && !isViewMode && (
              <p className="text-sm text-gray-500 mt-2">
                Email cannot be changed after user creation.
              </p>
            )}
          </div>
        </div>

        <h3 className="font-semibold mb-3">Assign Roles:</h3>
        <div className="border rounded-md overflow-hidden">
          {roles.length === 0 ? (
            <div className="p-4 text-gray-500">No active roles found.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-start gap-3 border rounded-md p-3 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-1"
                    checked={selectedRoleIds.has(role.id)}
                    disabled={isViewMode}
                    onChange={() => toggleRole(role.id)}
                  />
                  <span>
                    <span className="block font-medium">{role.name}</span>
                    <span className="block text-sm text-gray-500">
                      {role.description || "No description"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {!isViewMode && (
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="btn btn-reset-filter justify-center"
              onClick={() => navigate("/admin-users")}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn justify-center ${
                id ? "btn-edit text-white" : "btn-primary"
              } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : id
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUserForm;
